// src/admin/ItemManagementPage.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    getAllItems,
    // updateItem, // <-- Remove this if no longer needed for general updates
    deleteItem,
    addItem, // <-- Import addItem
    refreshItems,
    updateItemFlags, // <-- Import the new function
    updateItemTag, // <-- Import the new function for item tags
    purgeAllItems, // <-- Import the new purge function
} from '../api'; // Adjust path as needed

// --- Material UI Imports ---
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import RefreshIcon from '@mui/icons-material/Refresh';
import Checkbox from '@mui/material/Checkbox';
import Pagination from '@mui/material/Pagination';
import Chip from '@mui/material/Chip';
import LockClockIcon from '@mui/icons-material/LockClock';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { alpha } from '@mui/material/styles';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add'; // <-- Import Add Icon
import Snackbar from '@mui/material/Snackbar';
// --- NEW: Imports for Item Tag Select ---
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Dialog from '@mui/material/Dialog'; // <-- For Purge Modal
import DialogActions from '@mui/material/DialogActions'; // <-- For Purge Modal
import DialogContent from '@mui/material/DialogContent'; // <-- For Purge Modal
import DialogContentText from '@mui/material/DialogContentText'; // <-- For Purge Modal
import DialogTitle from '@mui/material/DialogTitle'; // <-- For Purge Modal
import ItemAddModal from './ItemAddModal'; // <-- Import the new Add Modal
// --- END NEW ---

const ITEMS_PER_PAGE = 50;

// Helper function to get Steam community image URL (consistent with InventoryPage)
const getSteamImageUrl = (iconUrlPath, sizeSuffix = '/96fx96f') => {
    // ... (keep existing function)
    if (!iconUrlPath) return '';
    if (iconUrlPath.startsWith('http')) {
        if (iconUrlPath.includes('community.cloudflare.steamstatic.com/economy/image/') && !iconUrlPath.match(/\/\d+fx\d+f/)) {
            return `${iconUrlPath}${sizeSuffix}`;
        }
        return iconUrlPath;
    }
    if (iconUrlPath.startsWith('//')) {
        return `https:${iconUrlPath}${sizeSuffix}`;
    }
    if (iconUrlPath.startsWith('ttps://')) {
        return `h${iconUrlPath}${sizeSuffix}`;
    }
    return `https://community.cloudflare.steamstatic.com/economy/image/${iconUrlPath}${sizeSuffix}`;
};

// Helper function to format date/time
const formatDateTime = (dateString) => {
    // ... (keep existing function)
    if (!dateString) return 'N/A';
    try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) {
            return 'Invalid Date';
        }
        return date.toLocaleString(undefined, {
            year: 'numeric', month: 'short', day: 'numeric',
            hour: 'numeric', minute: '2-digit', hour12: true
        });
    } catch (e) {
        console.error("Error formatting date:", e);
        return 'Invalid Date';
    }
};

// --- NEW: Helper function to format wear (optional, but good practice) ---
const formatWear = (wearValue) => {
    if (wearValue === null || wearValue === undefined || wearValue === '') {
        return 'N/A';
    }
    // Example: Format to 6 decimal places if it's a number
    if (typeof wearValue === 'number') {
        return wearValue.toFixed(6);
    }
    // Otherwise, return the value as is (might be a string like "Factory New")
    return String(wearValue);
};
// --- END NEW ---

// --- Purge Confirmation Modal Component ---
function PurgeConfirmationModal({ open, onClose, onConfirm, isPurging }) {
    const [confirmationInput, setConfirmationInput] = useState('');
    const [inputError, setInputError] = useState('');

    const handleInputChange = (event) => {
        setConfirmationInput(event.target.value);
        if (inputError) {
            setInputError(''); // Clear error when user types
        }
    };

    const handleConfirmClick = () => {
        if (confirmationInput !== 'Purge') {
            setInputError('You must type "Purge" exactly to confirm.');
            return;
        }
        onConfirm(); // Call the parent's confirm handler
    };

    // Reset input when modal closes or opens
    useEffect(() => {
        if (!open) {
            setConfirmationInput('');
            setInputError('');
        }
    }, [open]);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ bgcolor: 'error.dark', color: 'white' }}>
                Confirm Purge All Items
            </DialogTitle>
            <DialogContent sx={{ pt: '20px !important' }}> {/* Add padding top */}
                <Alert severity="error" sx={{ mb: 2 }}>
                    <strong>DANGER:</strong> This action is <strong>IRREVERSIBLE</strong>.
                    It will permanently delete <strong>ALL</strong> items from the main item database AND <strong>ALL</strong> user inventories.
                </Alert>
                <DialogContentText sx={{ mb: 2 }}>
                    This action cannot be undone. Please be absolutely sure before proceeding.
                    To confirm, type the word "<strong>Purge</strong>" (case-sensitive) into the box below.
                </DialogContentText>
                <TextField
                    autoFocus
                    margin="dense"
                    id="purge-confirm"
                    label='Type "Purge" to confirm'
                    type="text"
                    fullWidth
                    variant="outlined"
                    value={confirmationInput}
                    onChange={handleInputChange}
                    error={!!inputError}
                    helperText={inputError}
                    disabled={isPurging}
                />
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
                <Button onClick={onClose} color="inherit" disabled={isPurging}>Cancel</Button>
                <Button onClick={handleConfirmClick} color="error" variant="contained" disabled={isPurging || confirmationInput !== 'Purge'} startIcon={isPurging ? <CircularProgress size={20} color="inherit" /> : null}>
                    {isPurging ? 'Purging...' : 'Confirm Purge'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

// --- Main Item Management Page Component ---
function ItemManagementPage() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchQuery, setSearchQuery] = useState('');
    const [modifiedAssetIds, setModifiedAssetIds] = useState(new Set());
    const [isSaving, setIsSaving] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    // --- NEW: State for Purge Action ---
    const [isPurgeModalOpen, setIsPurgeModalOpen] = useState(false);
    const [isPurging, setIsPurging] = useState(false);
    // --- END NEW ---

    // --- NEW: State for Add Modal ---
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    // --- END NEW ---

    // --- NEW: State for inline updates (Item Tag) ---
    const [rowLoading, setRowLoading] = useState({}); // Track loading state per row, e.g., { assetId: true }
    const [rowError, setRowError] = useState({});     // Track error state per row, e.g., { assetId: 'Update failed' }
    // --- END NEW ---

    // Fetch items function
    const fetchItems = useCallback(async (showLoading = true) => {
        if (showLoading) setLoading(true);
        setError(null);
        try {
            const data = await getAllItems();
            // Ensure itemTag is present, default to "" if missing
            const processedData = (data || []).map(item => ({
                ...item,
                itemTag: item.itemTag ?? "",
            }));
            // Sort by assetid initially
            const sortedData = processedData.sort((a, b) => (a.assetid > b.assetid ? 1 : -1));
            setItems(sortedData);
            setCurrentPage(1);
            setSearchQuery('');
            setModifiedAssetIds(new Set());
            setRowLoading({}); // Clear row loading state on refresh
            setRowError({});   // Clear row error state on refresh
        } catch (err) {
            setError('Failed to load items. Please try refreshing.');
            console.error("Fetch items error:", err.response?.data || err.message || err);
        } finally {
            if (showLoading) setLoading(false);
        }
    }, []);

    // Initial fetch
    useEffect(() => {
        document.title = 'SpinnerCS - Admin: Items';
        fetchItems();
    }, [fetchItems]);

    // --- Action Handlers ---

    // handleRefreshItems
    const handleRefreshItems = async () => {
        if (modifiedAssetIds.size > 0) {
            const confirmDiscard = window.confirm(
                "You have unsaved flag changes. Are you sure you want to sync and discard these changes?"
            );
            if (!confirmDiscard) return;
        }
        if (Object.values(rowLoading).some(v => v)) {
            const confirmDiscard = window.confirm(
                "An item tag update is in progress. Are you sure you want to sync and potentially interrupt it?"
            );
            if (!confirmDiscard) return;
        }

        const confirmRefresh = window.confirm(
            "Are you sure you want to sync items from the source?\n" +
            "This will sync the database with the current bot inventories and may take some time."
        );
        if (!confirmRefresh) return;

        setRefreshing(true);
        setError(null);
        try {
            await refreshItems();
            await fetchItems(false); // Fetch items again after refresh
            setSnackbar({ open: true, message: 'Items synced successfully!', severity: 'success' });
        } catch (err) {
            const errMsg = err.response?.status === 503
                ? 'Refresh service is unavailable. Please try again later.'
                : 'Failed to refresh items.';
            setError(errMsg);
            console.error("Refresh items error:", err.response?.data || err.message || err);
            setSnackbar({ open: true, message: `Sync failed: ${errMsg}`, severity: 'error' });
        } finally {
            setRefreshing(false);
        }
    };

    // Handle Checkbox Change (for flags)
    const handleCheckboxChange = (assetId, fieldName, checked) => {
        setItems(prevItems =>
            prevItems.map(item =>
                item.assetid === assetId ? { ...item, [fieldName]: checked } : item
            )
        );
        setModifiedAssetIds(prevIds => new Set(prevIds).add(assetId));
    };

    // Handle Save Changes (for flags)
    const handleSaveChanges = async () => {
        setIsSaving(true);
        setError(null);
        const itemsToUpdate = items.filter(item => modifiedAssetIds.has(item.assetid));

        if (itemsToUpdate.length === 0) {
            setIsSaving(false);
            setSnackbar({ open: true, message: 'No flag changes to save.', severity: 'info' });
            return;
        }

        const updatePromises = itemsToUpdate.map(item => {
            const flagsPayload = {
                excludedFromSpin: item.excludedFromSpin || false,
                waitingForTrade: item.waitingForTrade || false,
            };
            return updateItemFlags(item.assetid, flagsPayload).catch(err => ({
                assetid: item.assetid,
                error: err,
            }));
        });

        try {
            const results = await Promise.all(updatePromises);
            const failedUpdates = results.filter(result => result?.error);

            if (failedUpdates.length > 0) {
                console.error("Some items failed to update flags:", failedUpdates);
                setError(`Failed to save flags for ${failedUpdates.length} item(s). Check console for details.`);
                setSnackbar({ open: true, message: `Failed to save flags for ${failedUpdates.length} item(s).`, severity: 'error' });
                // Keep modified IDs for failed items? Or clear all? Clearing all for now.
                setModifiedAssetIds(new Set());
            } else {
                setModifiedAssetIds(new Set());
                setSnackbar({ open: true, message: 'Flags saved successfully!', severity: 'success' });
            }
        } catch (err) {
            console.error("Error saving flags:", err);
            setError('An unexpected error occurred while saving flags.');
            setSnackbar({ open: true, message: 'An unexpected error occurred.', severity: 'error' });
        } finally {
            setIsSaving(false);
        }
    };

    // --- NEW: Handler for Item Tag Change ---
    const handleItemTagChange = useCallback(async (assetId, newItemTag) => {
        setRowLoading(prev => ({ ...prev, [assetId]: true }));
        setRowError(prev => ({ ...prev, [assetId]: null }));
        try {
            await updateItemTag(assetId, newItemTag);
            setSnackbar({ open: true, message: 'Item tag updated successfully', severity: 'success' });
            // Update local state immediately
            setItems(prevItems =>
                prevItems.map(item =>
                    item.assetid === assetId ? { ...item, itemTag: newItemTag } : item
                )
            );
        } catch (err) {
            const message = err.response?.data?.message || err.message || 'Failed to update item tag';
            console.error(`Error updating tag for item ${assetId}:`, err);
            setRowError(prev => ({ ...prev, [assetId]: message }));
            setSnackbar({ open: true, message: message, severity: 'error' });
            // Optionally revert local state change on error
            // fetchItems(false); // Or refetch to ensure consistency
        } finally {
            setRowLoading(prev => ({ ...prev, [assetId]: false }));
        }
    }, []); // No dependencies needed if not using enqueueSnackbar from notistack
    // --- END NEW ---

    // --- NEW: Add Item Handlers ---
    const handleOpenAddModal = () => {
        setIsAddModalOpen(true);
    };

    const handleCloseAddModal = () => {
        setIsAddModalOpen(false);
    };

    // --- END NEW ---

    // --- NEW: Purge Handlers ---
    const handleOpenPurgeModal = () => {
        // Add extra checks before even opening the modal
        if (loading || refreshing || isSaving || Object.values(rowLoading).some(v => v)) {
            setSnackbar({ open: true, message: 'Cannot purge while other operations are in progress.', severity: 'warning' });
            return;
        }
        setIsPurgeModalOpen(true);
    };

    const handleClosePurgeModal = () => {
        setIsPurgeModalOpen(false);
    };

    const handleConfirmPurge = async () => {
        setIsPurging(true);
        setError(null); // Clear previous main errors
        try {
            await purgeAllItems();
            setSnackbar({ open: true, message: 'All items purged successfully!', severity: 'success' });
            setIsPurgeModalOpen(false); // Close modal on success
            await fetchItems(false); // Refresh the item list (should be empty)
        } catch (err) {
            const errMsg = err.response?.data?.message || err.message || 'Failed to purge items.';
            setSnackbar({ open: true, message: `Purge failed: ${errMsg}`, severity: 'error' });
            // Keep the modal open on error so the user sees the context
        } finally {
            setIsPurging(false);
        }
    };
    // --- END NEW ---

    // handleDeleteItem
    const handleDeleteItem = async (assetId, itemName) => {
        if (modifiedAssetIds.has(assetId)) {
            const confirmDiscard = window.confirm(
                `Item "${itemName || assetId}" has unsaved flag changes. Are you sure you want to delete it and discard these changes?`
            );
            if (!confirmDiscard) return;
        }
        if (rowLoading[assetId]) {
            const confirmDiscard = window.confirm(
                `Item "${itemName || assetId}" tag update is in progress. Are you sure you want to delete it?`
            );
            if (!confirmDiscard) return;
        }

        const confirmDelete = window.confirm(`Are you sure you want to delete item "${itemName || assetId}"? This action cannot be undone.`);
        if (confirmDelete) {
            setLoading(true); // Use main loading indicator for delete
            setError(null);
            try {
                await deleteItem(assetId);
                setItems(prevItems => prevItems.filter(i => i.assetid !== assetId));
                setModifiedAssetIds(prevIds => {
                    const newIds = new Set(prevIds);
                    newIds.delete(assetId);
                    return newIds;
                });
                // Also clear any row-specific state for the deleted item
                setRowLoading(prev => { const newState = { ...prev }; delete newState[assetId]; return newState; });
                setRowError(prev => { const newState = { ...prev }; delete newState[assetId]; return newState; });
                setSnackbar({ open: true, message: `Item "${itemName || assetId}" deleted.`, severity: 'success' });
            } catch (err) {
                setError(`Failed to delete item ${assetId}.`);
                console.error(`Delete item ${assetId} error:`, err.response?.data || err.message || err);
                setSnackbar({ open: true, message: `Failed to delete item ${assetId}.`, severity: 'error' });
            } finally {
                setLoading(false);
            }
        }
    };

    // --- Search Handler ---
    const handleSearchChange = (event) => {
        if (modifiedAssetIds.size > 0) {
            const confirmDiscard = window.confirm(
                "You have unsaved flag changes that might be hidden by searching. Are you sure you want to discard these changes?"
            );
            if (!confirmDiscard) return;
            setModifiedAssetIds(new Set());
        }
        if (Object.values(rowLoading).some(v => v)) {
            const confirmDiscard = window.confirm(
                "An item tag update is in progress. Searching might hide the item. Are you sure you want to continue?"
            );
            if (!confirmDiscard) return;
            // Optionally cancel the ongoing update? For now, just proceed.
        }
        setSearchQuery(event.target.value);
        setCurrentPage(1);
    };

    // --- Filtering and Pagination Logic ---
    const filteredItems = useMemo(() => {
        if (!searchQuery) {
            return items;
        }
        const lowerCaseQuery = searchQuery.toLowerCase();
        return items.filter(item =>
            item.name?.toLowerCase().includes(lowerCaseQuery) ||
            item.assetid?.toString().includes(lowerCaseQuery) ||
            item.accountId?.toString().includes(lowerCaseQuery) ||
            item.itemTag?.toLowerCase().includes(lowerCaseQuery) // Allow searching by tag
        );
    }, [items, searchQuery]);

    const totalFilteredItems = filteredItems.length;
    const pageCount = Math.ceil(totalFilteredItems / ITEMS_PER_PAGE);
    const itemsOnPage = useMemo(() => {
        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        const endIndex = startIndex + ITEMS_PER_PAGE;
        // Get the assetids for the current page based on the filtered list
        const pageAssetIds = new Set(filteredItems.slice(startIndex, endIndex).map(i => i.assetid));
        // Return the full item objects from the original items state that match the page's assetids
        // This ensures we render items with potentially unsaved changes correctly
        return items.filter(item => pageAssetIds.has(item.assetid));
    }, [filteredItems, currentPage, items]); // Depend on items to reflect unsaved changes

    const handlePageChange = (event, value) => {
        if (modifiedAssetIds.size > 0) {
            const confirmDiscard = window.confirm(
                "You have unsaved flag changes that might be hidden by changing pages. Are you sure you want to discard these changes?"
            );
            if (!confirmDiscard) return;
            setModifiedAssetIds(new Set());
        }
        if (Object.values(rowLoading).some(v => v)) {
            const confirmDiscard = window.confirm(
                "An item tag update is in progress. Changing pages might hide the item. Are you sure you want to continue?"
            );
            if (!confirmDiscard) return;
            // Optionally cancel the ongoing update? For now, just proceed.
        }
        setCurrentPage(value);
        window.scrollTo(0, 0);
    };

    // --- Snackbar close handler ---
    const handleCloseSnackbar = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setSnackbar(prev => ({ ...prev, open: false }));
    };


    // --- Render Logic ---
    if (loading && items.length === 0) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
    }

    const hasUnsavedFlagChanges = modifiedAssetIds.size > 0;

    return (
        // The main Box component takes the available width within its parent (AdminLayout's main content area)
        <Box>
            {/* Header and Action Buttons */}
            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                spacing={2}
                sx={{ mb: 2 }}
            >
                <Typography variant="h4" gutterBottom sx={{ mb: { xs: 1, sm: 0 } }}>
                    Item Management ({totalFilteredItems}) {/* Show total count */}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <TextField
                        label="Search Name/Asset/Bot/Tag" // Updated label
                        variant="outlined"
                        size="small"
                        value={searchQuery}
                        onChange={handleSearchChange}
                        disabled={loading || refreshing || isSaving || isPurging || Object.values(rowLoading).some(v => v)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                        sx={{ width: { xs: '100%', sm: 250, md: 300 } }}
                    />
                    {/* --- Add Item Button (Already exists) --- */}
                    <Button
                        variant="contained"
                        color="secondary"
                        startIcon={<AddIcon />}
                        onClick={handleOpenAddModal}
                        disabled={loading || refreshing || isSaving || isPurging || Object.values(rowLoading).some(v => v)}
                    >
                        Add Item
                    </Button>
                    {/* Save Changes Button (for flags) */}
                    <Button
                        variant="contained"
                        color="success"
                        startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                        onClick={handleSaveChanges} // Keep this for flags
                        disabled={!hasUnsavedFlagChanges || loading || refreshing || isSaving || Object.values(rowLoading).some(v => v)}
                        sx={{ minWidth: 150 }}
                    >
                        {isSaving ? 'Saving Flags...' : `Save Flags (${modifiedAssetIds.size})`}
                    </Button>
                    {/* Sync Items Button */}
                    <Tooltip title="Sync items in the database with the current bot inventories.">
                        <span>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleRefreshItems} // Keep this for sync
                                disabled={loading || refreshing || isSaving || isPurging || Object.values(rowLoading).some(v => v)}
                                startIcon={refreshing ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
                                size="medium"
                            >
                                {refreshing ? 'Syncing...' : 'Sync Items'}
                            </Button>
                        </span>
                    </Tooltip>
                    {/* --- NEW: Purge Button --- */}
                    <Tooltip title="[DANGEROUS] Permanently delete ALL items from the database and user inventories.">
                        <span>
                            <Button
                                variant="contained"
                                color="error"
                                onClick={handleOpenPurgeModal}
                                disabled={loading || refreshing || isSaving || isPurging || Object.values(rowLoading).some(v => v)}
                                size="medium"
                            >
                                Purge All Items
                            </Button>
                        </span>
                    </Tooltip>
                </Stack>
            </Stack>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {hasUnsavedFlagChanges && !isSaving && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    You have unsaved flag changes. Click "Save Flags" to persist them.
                </Alert>
            )}

            {/* Table Container - This component handles the horizontal scrolling */}
            <TableContainer component={Paper}>
                {/* The Table's minWidth forces the container to scroll if needed */}
                <Table sx={{ minWidth: 1300 }} aria-label="item table" size="small"> {/* Increased minWidth */}
                    <TableHead>
                        <TableRow sx={{ '& th': { fontWeight: 'bold' } }}>
                            <TableCell>Icon</TableCell>
                            <TableCell>Name</TableCell>
                            <TableCell>Wear</TableCell>
                            <TableCell>Asset ID</TableCell>
                            <TableCell>Account ID (Bot)</TableCell>
                            {/* --- NEW: Item Tag Header --- */}
                            <TableCell>Tag</TableCell>
                            <TableCell align="center">Withdrawn</TableCell> {/* <-- Add new header */}
                            {/* --- END NEW --- */}
                            <TableCell align="center">Tradable Status</TableCell>
                            <TableCell align="center">Excluded (Spin)</TableCell>
                            <TableCell align="center">Waiting (Trade)</TableCell>
                            <TableCell align="center">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {itemsOnPage.map((item) => {
                            const isRowLoading = rowLoading[item.assetid];
                            const rowErrorMsg = rowError[item.assetid];
                            return (
                                <TableRow
                                    key={item.assetid}
                                    hover
                                    sx={(theme) => ({
                                        '&:last-child td, &:last-child th': { border: 0 },
                                        backgroundColor: modifiedAssetIds.has(item.assetid)
                                            ? alpha(theme.palette.info.light, 0.15) // Highlight unsaved flag changes
                                            : rowErrorMsg
                                                ? alpha(theme.palette.error.light, 0.15) // Highlight rows with tag update errors
                                                : (item.tradable === false
                                                    ? alpha(theme.palette.warning.light, 0.1)
                                                    : (item.excludedFromSpin ? alpha('#ffeb3b', 0.1) : 'inherit')),
                                    })}
                                >
                                    <TableCell sx={{ p: 0.5 }}>
                                        <Avatar
                                            variant="square"
                                            src={getSteamImageUrl(item.icon_url)}
                                            alt={item.name?.substring(0, 1) || '?'}
                                            sx={{ width: 40, height: 40, bgcolor: 'grey.700' }}
                                        />
                                    </TableCell>
                                    <TableCell sx={{ color: item.name_color ? `#${item.name_color}` : 'inherit', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        <Tooltip title={item.name || 'N/A'} placement="top-start">
                                            <span>{item.name || 'N/A'}</span>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell sx={{ maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        <Tooltip title={`Wear: ${formatWear(item.wear)}`} placement="top">
                                            <Typography variant="caption" color="text.secondary">
                                                {formatWear(item.wear)}
                                            </Typography>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell>{item.assetid}</TableCell>
                                    <TableCell>{item.accountId}</TableCell>
                                    {/* --- NEW: Item Tag Cell --- */}
                                    <TableCell sx={{ width: 150 }}>
                                        <Tooltip title={rowErrorMsg || ''} placement="top" arrow>
                                            <FormControl fullWidth size="small" error={!!rowErrorMsg} disabled={isRowLoading || loading || refreshing || isSaving || isPurging}>
                                                <Select
                                                    value={item.itemTag ?? ""}
                                                    onChange={(e) => handleItemTagChange(item.assetid, e.target.value)}
                                                    displayEmpty
                                                    inputProps={{ 'aria-label': 'Item Tag' }}
                                                    sx={{ height: 35 }} // Adjust height
                                                >
                                                    <MenuItem value=""><em>None</em></MenuItem>
                                                    <MenuItem value="Staging">Staging</MenuItem>
                                                    <MenuItem value="Featured">Featured</MenuItem>
                                                </Select>
                                                {isRowLoading && (
                                                    <CircularProgress size={20} sx={{ position: 'absolute', top: '50%', left: '50%', marginTop: '-10px', marginLeft: '-10px' }} />
                                                )}
                                            </FormControl>
                                        </Tooltip>
                                    </TableCell>
                                    {/* --- END NEW --- */}
                                    {/* --- NEW: Withdrawn Status Cell --- */}
                                    <TableCell align="center">
                                        {item.isWithdrawn ?
                                            <Chip label="Yes" color="warning" size="small" variant="outlined" /> :
                                            <Chip label="No" size="small" variant="outlined" />}
                                    </TableCell>
                                    <TableCell align="center" sx={{ minWidth: 180 }}>
                                        {item.tradable === false && item.tradableDate ? (
                                            <Tooltip title={`Tradable after: ${formatDateTime(item.tradableDate)}`}>
                                                <Chip
                                                    icon={<LockClockIcon fontSize="small" />}
                                                    label={`Until ${new Date(item.tradableDate).toLocaleDateString()}`}
                                                    color="warning" size="small" variant="outlined"
                                                />
                                            </Tooltip>
                                        ) : item.tradable === false ? (
                                            <Chip label="No" color="error" size="small" variant="outlined" />
                                        ) : item.tradable === true ? (
                                            <Chip icon={<CheckCircleOutlineIcon fontSize="small" />} label="Yes" color="success" size="small" variant="outlined" />
                                        ) : (
                                            <Typography variant="caption" color="text.secondary">-</Typography>
                                        )}
                                    </TableCell>
                                    {/* Checkboxes (Flags) */}
                                    <TableCell align="center">
                                        <Tooltip title={item.excludedFromSpin ? "Click to include in spin" : "Click to exclude from spin"}>
                                            <Checkbox
                                                checked={item.excludedFromSpin || false}
                                                onChange={(e) => handleCheckboxChange(item.assetid, 'excludedFromSpin', e.target.checked)}
                                                size="small"
                                                disabled={loading || refreshing || isSaving || isPurging || isRowLoading}
                                            />
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell align="center">
                                        <Tooltip title={item.waitingForTrade ? "Click to mark as NOT waiting" : "Click to mark as waiting for trade"}>
                                            <Checkbox
                                                checked={item.waitingForTrade || false}
                                                onChange={(e) => handleCheckboxChange(item.assetid, 'waitingForTrade', e.target.checked)}
                                                size="small"
                                                disabled={loading || refreshing || isSaving || isPurging || isRowLoading}
                                            />
                                        </Tooltip>
                                    </TableCell>
                                    {/* Actions */}
                                    <TableCell align="center">
                                        <Tooltip title="Delete Item">
                                            <IconButton
                                                onClick={() => handleDeleteItem(item.assetid, item.name)}
                                                color="error" size="small"
                                                disabled={loading || refreshing || isSaving || isPurging || isRowLoading}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Pagination Controls */}
            {pageCount > 1 && (
                <Stack spacing={2} sx={{ mt: 3, mb: 2, alignItems: 'center' }}>
                    <Pagination
                        count={pageCount}
                        page={currentPage}
                        onChange={handlePageChange}
                        color="primary"
                        showFirstButton
                        showLastButton
                        disabled={loading || refreshing || isSaving || isPurging || Object.values(rowLoading).some(v => v)}
                    />
                </Stack>
            )}

            {/* Empty state message */}
            {totalFilteredItems === 0 && !loading && !error && (
                <Typography sx={{ mt: 3, textAlign: 'center', color: 'text.secondary' }}>
                    {searchQuery ? `No items found matching "${searchQuery}".` : 'No items found. Try syncing items from bots.'}
                </Typography>
            )}

            {/* Snackbar for Feedback */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>

            {/* --- NEW: Purge Confirmation Modal --- */}
            <PurgeConfirmationModal
                open={isPurgeModalOpen}
                onClose={handleClosePurgeModal}
                onConfirm={handleConfirmPurge}
                isPurging={isPurging}
            />

            {/* --- NEW: Add Item Modal --- */}
            <ItemAddModal
                open={isAddModalOpen}
                onClose={handleCloseAddModal}
                onAddSuccess={() => { fetchItems(false); setSnackbar({ open: true, message: 'Item added successfully!', severity: 'success' }); }}
            />
        </Box>
    );
}

export default ItemManagementPage;
